/**
 * Client-side trace ID utilities for correlating frontend and backend logs.
 *
 * Generates a per-session trace ID that can be attached to both log entries
 * and outgoing HTTP headers (X-Trace-ID), matching the backend convention
 * in pkg/trace/trace.go.
 */

const SESSION_TRACE_PREFIX = 'client-';
const PAGE_VIEW_PREFIX = 'page-';
const SESSION_STORAGE_KEY = 'colix_session_trace_id';

/** Generate a UUID v4 (browser crypto API, fallback to Math.random) */
function uuid(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

let sessionTraceId: string | null = null;
let pageViewId: string | null = null;

function readSessionStorage(key: string): string | null {
  if (typeof sessionStorage === 'undefined') return null;

  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSessionStorage(key: string, value: string): void {
  if (typeof sessionStorage === 'undefined') return;

  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Ignore storage failures in locked-down browsers/private mode.
  }
}

/**
 * Get or create a session-scoped trace ID.
 * Persists for the lifetime of the browser tab when sessionStorage is available.
 */
export function getSessionTraceId(): string {
  if (!sessionTraceId) {
    sessionTraceId =
      readSessionStorage(SESSION_STORAGE_KEY) ?? (SESSION_TRACE_PREFIX + uuid());
    writeSessionStorage(SESSION_STORAGE_KEY, sessionTraceId);
  }
  return sessionTraceId;
}

/**
 * Get or create a page-view-scoped identifier.
 * Resets on full-page navigations/reloads.
 */
export function getPageViewId(): string {
  if (!pageViewId) {
    pageViewId = PAGE_VIEW_PREFIX + uuid();
  }

  return pageViewId;
}

/**
 * Generate a fresh trace ID for a single request span.
 * Use this to correlate a specific API call with its backend logs.
 */
export function newTraceId(): string {
  return SESSION_TRACE_PREFIX + uuid();
}

/** The header name matching the Go backend convention */
export const TRACE_ID_HEADER = 'X-Trace-ID';

export function getBrowserLocale(): string | undefined {
  return typeof navigator !== 'undefined' ? navigator.language : undefined;
}

export function getBrowserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}
